import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const SearchableSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt._id === value);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-sm">{selectedOption ? selectedOption.name : placeholder}</span>
        <i className={`bi bi-chevron-down text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.map(option => (
              <button
                key={option._id}
                type="button"
                onClick={() => {
                  onChange(option._id);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="w-full px-2 sm:px-4 py-2 text-sm text-left hover:bg-[#009ef7]/10 transition-colors"
              >
                {option.name}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-2 sm:px-4 py-2 text-sm text-gray-500">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AddCity = () => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedStates, setSelectedStates] = useState([]);
  const [cityData, setCityData] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await apiService.get('countryList');
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await apiService.post('stateLocationList', {
        countryId,
        type: 'country'
      });
      
      if (response.message === 'success') {
        setStates(response.data.states || []);
        setSelectedStates(response.data.savedStates || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    setSelectedCountry(countryId);
    setSelectedState('');
    setSelectedStates([]);
    if (countryId) {
      fetchStates(countryId);
    } else {
      setStates([]);
    }
  };

  const handleStateCheckbox = (stateId) => {
    setSelectedStates(prev => 
      prev.includes(stateId) 
        ? prev.filter(id => id !== stateId)
        : [...prev, stateId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cityData.trim()) {
      toast.error('Please enter city data');
      return;
    }

    try {
      JSON.parse(cityData);
    } catch (error) {
      toast.error('Invalid JSON format');
      return;
    }

    setSubmitting(true);

    const formData = new URLSearchParams();
    formData.append('countryId', selectedCountry);
    formData.append('stateId', selectedState);
    selectedStates.forEach(stateId => {
      formData.append('states[]', stateId);
    });
    formData.append('data', cityData);

    try {
      const response = await apiService.post('addCity', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      if (response.status) {
        toast.success(response.message);
        setCityData('');
        setSelectedState('');
        if (response.data && response.data.length > 0) {
          setSelectedStates(response.data);
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to add city');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-0">
      <div className="mb-2">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Add City</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Country</label>
              <SearchableSelect
                value={selectedCountry}
                onChange={(value) => {
                  setSelectedCountry(value);
                  setSelectedState('');
                  setSelectedStates([]);
                  if (value) {
                    fetchStates(value);
                  } else {
                    setStates([]);
                  }
                }}
                options={countries}
                placeholder="Select Country"
              />
            </div>

            {states.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select States</label>
                <div className="flex flex-wrap gap-4">
                  {states.map(state => (
                    <div key={state._id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`state_${state._id}`}
                        checked={selectedStates.includes(state._id)}
                        onChange={() => handleStateCheckbox(state._id)}
                        className="w-4 h-4 text-[#009ef7] border-gray-300 rounded focus:ring-[#009ef7]"
                      />
                      <label htmlFor={`state_${state._id}`} className="ml-2 text-sm text-gray-700">
                        {state.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select State/Region</label>
              <SearchableSelect
                value={selectedState}
                onChange={setSelectedState}
                options={states}
                placeholder="Select State/Region"
                disabled={!selectedCountry}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                JSON City Data <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 ml-2">[{`{"id":1,"name":"City Name"}`}]</span>
              </label>
              <input
                type="text"
                value={cityData}
                onChange={(e) => setCityData(e.target.value)}
                placeholder='[{"id":1,"name":"City Name"}]'
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent font-mono text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCity;
